import { logger } from '../utils/logger';
import { PublicClientApplication, AccountInfo, AuthenticationResult, SilentRequest } from "@azure/msal-browser";
import { msalConfig, loginRequest, apiConfig } from "../authConfig";

class AuthService {
    private msalInstance: PublicClientApplication;

    constructor() {
        this.msalInstance = new PublicClientApplication(msalConfig);
    }

    async initialize(): Promise<void> {
        await this.msalInstance.initialize();
    }

    getInstance(): PublicClientApplication {
        return this.msalInstance;
    }

    async loginPopup(): Promise<AuthenticationResult> {
        try {
            const response = await this.msalInstance.loginPopup(loginRequest);
            return response;
        } catch (error) {
            logger.error("Login failed:", error);
            throw error;
        }
    }

    async loginRedirect(): Promise<void> {
        try {
            await this.msalInstance.loginRedirect(loginRequest);
        } catch (error) {
            logger.error("Login redirect failed:", error);
            throw error;
        }
    }

    async logout(): Promise<void> {
        try {
            await this.msalInstance.logoutPopup();
        } catch (error) {
            logger.error("Logout failed:", error);
            throw error;
        }
    }

    getActiveAccount(): AccountInfo | null {
        return this.msalInstance.getActiveAccount();
    }

    getAllAccounts(): AccountInfo[] {
        return this.msalInstance.getAllAccounts();
    }

    async getAccessToken(): Promise<string | null> {
        const account = this.getActiveAccount();
        if (!account) {
            throw new Error("No active account found");
        }

        const silentRequest: SilentRequest = {
            scopes: apiConfig.scopes,
            account: account
        };

        try {
            const response = await this.msalInstance.acquireTokenSilent(silentRequest);
            return response.accessToken;
        } catch (error) {
            logger.error("Silent token acquisition failed:", error);
            // If silent request fails, try popup
            try {
                const response = await this.msalInstance.acquireTokenPopup({
                    scopes: apiConfig.scopes,
                    account: account
                });
                return response.accessToken;
            } catch (popupError) {
                logger.error("Popup token acquisition failed:", popupError);
                throw popupError;
            }
        }
    }

    /**
     * Silent-only token acquisition for background and best-effort calls.
     *
     * Never prompts: unlike getAccessToken() there is no popup fallback, and no
     * throw. Returns null when nobody is signed in, when silent acquisition
     * fails, or when it has not settled within timeoutMs (a stalled MSAL call
     * must not hold a chat turn or a log write hostage).
     */
    async getAccessTokenSilent(timeoutMs = 3000): Promise<string | null> {
        const account = this.getActiveAccount();
        if (!account) return null;

        let timer: ReturnType<typeof setTimeout> | undefined;
        try {
            const timeout = new Promise<null>((resolve) => {
                timer = setTimeout(() => resolve(null), timeoutMs);
            });
            const acquire = this.msalInstance
                .acquireTokenSilent({ scopes: apiConfig.scopes, account })
                .then((response) => response.accessToken || null);
            // A late rejection after the timeout won must not become unhandled.
            acquire.catch(() => undefined);
            return await Promise.race([acquire, timeout]);
        } catch (error) {
            logger.warn("Silent token acquisition failed (no prompt):", error);
            return null;
        } finally {
            if (timer !== undefined) clearTimeout(timer);
        }
    }

    /**
     * Silent re-acquisition that bypasses MSAL's token cache (forceRefresh), for
     * recovering from a 401. Without forceRefresh MSAL hands back the very token
     * the backend just rejected. Needs no user gesture, so it may run
     * automatically; never prompts and never throws. Returns null when nobody is
     * signed in, when the refresh fails, or when it has not settled within
     * timeoutMs.
     */
    async getAccessTokenForceRefresh(timeoutMs = 3000): Promise<string | null> {
        const account = this.getActiveAccount();
        if (!account) return null;

        let timer: ReturnType<typeof setTimeout> | undefined;
        try {
            const timeout = new Promise<null>((resolve) => {
                timer = setTimeout(() => resolve(null), timeoutMs);
            });
            const acquire = this.msalInstance
                .acquireTokenSilent({ scopes: apiConfig.scopes, account, forceRefresh: true })
                .then((response) => response.accessToken || null);
            acquire.catch(() => undefined);
            return await Promise.race([acquire, timeout]);
        } catch (error) {
            logger.warn("Forced silent token refresh failed (no prompt):", error);
            return null;
        } finally {
            if (timer !== undefined) clearTimeout(timer);
        }
    }

    /**
     * Interactive sign-in that skips the silent step. ONLY for a handler that
     * runs inside a user's own click (the "Sign in again" button): the popup is
     * opened before anything is awaited so the browser does not block it. Returns
     * null when nobody is signed in or the user cancels.
     */
    async acquireTokenInteractive(): Promise<string | null> {
        const account = this.getActiveAccount();
        if (!account) return null;
        try {
            const response = await this.msalInstance.acquireTokenPopup({ scopes: apiConfig.scopes, account });
            return response.accessToken || null;
        } catch (error) {
            logger.warn("Interactive sign-in did not complete:", error);
            return null;
        }
    }

    isAuthenticated(): boolean {
        const account = this.getActiveAccount();
        return account !== null;
    }

    getUserInfo(): { name?: string; email?: string; username?: string; objectId?: string } | null {
        const account = this.getActiveAccount();
        if (!account) return null;

        // Get the actual Azure AD object ID from idTokenClaims.oid
        // localAccountId can sometimes be the UPN/email instead of the object ID
        const objectId = (account.idTokenClaims as { oid?: string })?.oid || account.localAccountId;

        return {
            name: account.name,
            email: account.username,
            username: account.username,
            objectId: objectId
        };
    }

    getUserGroups(): string[] {
        const account = this.getActiveAccount();
        if (!account || !account.idTokenClaims) return [];

        // Get groups from ID token claims
        const claims = account.idTokenClaims as { groups?: string[] };
        return claims.groups || [];
    }

    /**
     * Whether group membership is actually known for the signed-in user.
     *
     * Azure AD omits the `groups` claim entirely and substitutes a
     * `_claim_names` / `_claim_sources` overage reference once a user is a
     * member of more than ~200 groups. In that case getUserGroups() returns []
     * — indistinguishable, on its face, from "member of nothing".
     *
     * Callers that gate on group membership must consult this first so they can
     * tell "no access" apart from "cannot tell". Resolving an overage requires
     * a Graph call (getMemberObjects); until that exists, treat overage as
     * unknown and fail closed.
     */
    hasResolvedGroups(): boolean {
        const account = this.getActiveAccount();
        if (!account || !account.idTokenClaims) return false;

        const claims = account.idTokenClaims as {
            groups?: string[];
            _claim_names?: Record<string, string>;
            _claim_sources?: Record<string, unknown>;
        };

        // Overage: groups moved to a Graph endpoint, claim is a pointer.
        if (claims._claim_names?.groups || claims._claim_sources) {
            logger.warn(
                'Groups claim is in overage (user is in >200 AD groups); ' +
                'app access cannot be determined from the token.'
            );
            return false;
        }

        return Array.isArray(claims.groups);
    }

    hasGroupAccess(groupId: string): boolean {
        const userGroups = this.getUserGroups();
        return userGroups.includes(groupId);
    }
}

export const authService = new AuthService();
